'use strict';

import React, { Component } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import moment from 'moment';
import redirectURL from '../redirectURL';
import {
  COLORS,
  MULTI_SELECT_STYLES,
  safeArray,
  groupBy,
  distinctValues,
  sum,
  average,
  round1,
  baseChartOptions,
  baseAxisLine,
  baseValueAxis,
  GridCard,
} from './dashboardShared';
import './performanceDashboardModern.css';

/*
 * CO2 Emission Dashboard - the 6th top-level dashboard. Maps to the PDF's
 * "CO2 Report" section and the screenshots shared directly: baseline
 * banner, 3 KPI cards, a movement-type donut, 4 monthly trend lines, two
 * "Total Dispatches" tables (LI+IA+VA and Numeric), a lane-wise route
 * table, and an entity-level summary table with 2024 vs 2025 comparisons.
 *
 * Sourced from consignments/getDataForEmissionDashboard (newly wired up in
 * Node - see consignmentsController.js), backed by a real, confirmed
 * sample response. Total_Emission is NOT computed here - it's a raw
 * pre-computed field (total_emisson, note the source typo) already present
 * per row, confirmed against the real sample:
 *   total_emisson = dist_in_km * total_weight * emission_factor / 1,000,000
 *   e.g. 179.618 * 0.28 * 90 / 1e6 = 0.004526 - matches exactly.
 * total_weight is confirmed in KG (matches the Power BI model's "Total
 * Weight In Kg's" field). There is NO invoice_time field on this feed, so
 * unlike every other dashboard there's no real Invoice Date basis to
 * toggle to - dispatch_date/dispatch_date1 is the only date field.
 *
 * base_line_emission/base_line_gross_co2 are real per-entity constants
 * (confirmed from the sample: 7.1% / 3089.75 for both LEGRAND and
 * INDOASIAN rows, 7.5% / 1023.04 for NUMERIC rows) - matching the PDF's
 * "LI+A+VA: 7.1% | Numeric: 7.5%" split exactly. So the baseline banner is
 * real data, not a hardcoded guess - VALRACK hasn't appeared in a sample
 * yet but is grouped with LI+A+VA per the PDF's own grouping.
 *
 * movement_type is sometimes populated directly (e.g. "Internal Movement"
 * in a real sample) and sometimes blank; when blank, Primary/Secondary is
 * derived from invoice_type via the same /stock transfer/i pattern used on
 * every other dashboard on this app.
 *
 * STILL BLOCKED (kept as pending placeholders below):
 * - Plant to RWHS widgets (x2): no "Route" field exists in the raw data
 *   (it's Power BI's own derived column) and no whitelist of which lanes
 *   count as "Plant to RWHS" has been provided.
 * - Entity Summary: no Area/CSR Area field anywhere in this feed - looks
 *   like separate master data that hasn't been located, plus the 2024 vs
 *   2025 comparison needs a wider historical query than a single date
 *   range widget naturally provides.
 */

const TRACKING_MODE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'FTL', label: 'FTL' },
  { value: 'PTL', label: 'PTL' },
];

const NUMERIC_ENTITY = 'NUMERIC';

function emissionOf(row) {
  const n = Number(row.total_emisson);
  return isNaN(n) ? 0 : n;
}

function weightKgOf(row) {
  const n = Number(row.total_weight);
  return isNaN(n) ? 0 : n;
}

function numOrZero(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// No invoice_time field exists on this feed (confirmed from real samples) -
// dispatch_date is the only usable date, with dispatch_date1 as a fallback
// when it's blank, same pattern used elsewhere.
function rowDateOf(row) {
  const value = row.dispatch_date || row.dispatch_date1;
  return value ? moment(value) : null;
}

function movementCategoryOf(row) {
  if (row.movement_type) return row.movement_type;
  return /stock transfer/i.test(row.invoice_type || '') ? 'Primary' : 'Secondary';
}

function entityGroupOf(row) {
  return (row.entity || '').toUpperCase() === NUMERIC_ENTITY ? 'NUMERIC' : 'LI+IA+VA';
}

function monthRange(start, end) {
  const months = [];
  let cursor = moment(start).startOf('month');
  const last = moment(end).startOf('month');
  while (cursor.isSameOrBefore(last)) {
    months.push(cursor.format('YYYY-MM'));
    cursor = cursor.add(1, 'month');
  }
  return months;
}

function roundN(n, dp) {
  const f = Math.pow(10, dp);
  return Math.round((n + Number.EPSILON) * f) / f;
}

export default class Co2DashboardModern extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      lastRefreshed: null,

      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        trackingMode: TRACKING_MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },
      filterOptions: {
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },

      rawRows: [],
      filteredRows: [],
      derived: null,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  buildPayload() {
    const f = this.state.filters;
    return {
      startDate: moment(f.startDate).format('YYYY-MM-DD'),
      endDate: moment(f.endDate).format('YYYY-MM-DD'),
    };
  }

  loadData = () => {
    this.setState({ loading: true, error: null });
    redirectURL.post('consignments/getDataForEmissionDashboard', this.buildPayload())
      .then((resp) => {
        const rows = safeArray(resp.data);
        rows.forEach((row) => { row.movement_category = movementCategoryOf(row); });
        const filterOptions = {
          entity: distinctValues(rows, 'entity').map((v) => ({ value: v, label: v })),
          movementType: distinctValues(rows, 'movement_category').map((v) => ({ value: v, label: v })),
          sourceZone: distinctValues(rows, 'consigner_zone').map((v) => ({ value: v, label: v })),
          sourceCity: distinctValues(rows, 'consigner_city').map((v) => ({ value: v, label: v })),
          destinationZone: distinctValues(rows, 'consignee_zone').map((v) => ({ value: v, label: v })),
          transporterCategory: distinctValues(rows, 'Transporter Type').map((v) => ({ value: v, label: v })),
          invoiceType: distinctValues(rows, 'invoice_type').map((v) => ({ value: v, label: v })),
          transporter: distinctValues(rows, 'transporter_name').map((v) => ({ value: v, label: v })),
        };
        this.setState({ loading: false, lastRefreshed: moment(), rawRows: rows, filterOptions });
        this.applyClientFilters(rows);
      })
      .catch((error) => {
        console.error('Error fetching CO2 dashboard data:', error);
        this.setState({ loading: false, error: 'Unable to load CO2 dashboard data. Please try again.' });
      });
  };

  // Belt-and-suspenders client-side date check on top of the server-side
  // startDate/endDate: every other "takes date params" endpoint on this app
  // turned out to silently ignore them at some point (getFTLVehicleUtilization,
  // getInvoiceData before its fix) - this hasn't been proven to have the same
  // issue, but re-checking here costs nothing and protects against it quietly
  // regressing.
  applyClientFilters = (rowsOverride) => {
    const rows = rowsOverride || this.state.rawRows;
    const f = this.state.filters;
    const rangeStart = moment(f.startDate).startOf('day');
    const rangeEnd = moment(f.endDate).endOf('day');
    const matches = (row, selection, field) => {
      if (!selection.length) return true;
      return selection.map((o) => o.value).includes(row[field]);
    };
    const filtered = rows.filter((row) => {
      const d = rowDateOf(row);
      if (!d || !d.isValid() || !d.isBetween(rangeStart, rangeEnd, null, '[]')) return false;
      return (
        matches(row, f.entity, 'entity')
        && matches(row, f.movementType, 'movement_category')
        && matches(row, f.sourceZone, 'consigner_zone')
        && matches(row, f.sourceCity, 'consigner_city')
        && matches(row, f.destinationZone, 'consignee_zone')
        && matches(row, f.transporterCategory, 'Transporter Type')
        && matches(row, f.invoiceType, 'invoice_type')
        && matches(row, f.transporter, 'transporter_name')
        && (f.trackingMode.value === 'All' || row.tracking_type === f.trackingMode.value)
      );
    });
    this.setState({ filteredRows: filtered, derived: this.computeDerived(filtered) });
  };

  // Aggregates one entity group (LI+IA+VA or NUMERIC) into the "Total
  // Dispatches" table's row shape. Aggregation choices, since none of these
  // are handed to us pre-aggregated:
  // - Total Emission / Total Weight (Tons): straight sums - real amounts,
  //   safe to add across rows.
  // - TCO2PerTon: recomputed from the summed totals (not averaged per-row),
  //   so it's consistent with "total emission over total tonnage" rather
  //   than an average of per-shipment ratios.
  // - Emission Reduction / Final Reduction: averaged across the group's
  //   rows (gross_co2_reduction / final_reduction are real per-row fields,
  //   but appear to be shipment/lane-level constants rather than something
  //   with an obviously "correct" weighting - plain average is the least
  //   presumptuous choice).
  // - TCo2-Ton Reduction: sum of tco2_in_tons_reduction (a real per-row
  //   amount in tons, sums sensibly).
  // - Cumulative Reduction: running total of TCo2-Ton Reduction across the
  //   months in chronological order.
  buildEntityGroupRows(rows, months) {
    const monthGroups = groupBy(rows, (r) => {
      const d = rowDateOf(r);
      return d ? d.format('YYYY-MM') : null;
    });
    let cumulative = 0;
    const monthlyRows = months.map((m) => {
      const items = safeArray(monthGroups.get(m));
      const totalEmission = sum(items.map(emissionOf));
      const totalWeightTons = sum(items.map(weightKgOf)) / 1000;
      const tco2PerTon = totalWeightTons > 0 ? totalEmission / totalWeightTons : 0;
      const emissionReductionPct = items.length ? average(items.map((r) => numOrZero(r.gross_co2_reduction) * 100)) : 0;
      const tco2TonReduction = sum(items.map((r) => numOrZero(r.tco2_in_tons_reduction)));
      const finalReductionPct = items.length ? average(items.map((r) => numOrZero(r.final_reduction) * 100)) : 0;
      cumulative += tco2TonReduction;
      return {
        yearMonth: moment(m, 'YYYY-MM').format('MMM YYYY'),
        totalEmission: roundN(totalEmission, 4),
        tco2PerTon: roundN(tco2PerTon, 4),
        emissionReductionPct: round1(emissionReductionPct),
        tco2TonReduction: roundN(tco2TonReduction, 4),
        totalWeightTons: round1(totalWeightTons),
        finalReductionPct: round1(finalReductionPct),
        cumulativeReduction: roundN(cumulative, 4),
      };
    });
    const totalEmissionAll = sum(rows.map(emissionOf));
    const totalWeightTonsAll = sum(rows.map(weightKgOf)) / 1000;
    const totalRow = {
      yearMonth: 'Total',
      totalEmission: roundN(totalEmissionAll, 4),
      tco2PerTon: roundN(totalWeightTonsAll > 0 ? totalEmissionAll / totalWeightTonsAll : 0, 4),
      emissionReductionPct: round1(rows.length ? average(rows.map((r) => numOrZero(r.gross_co2_reduction) * 100)) : 0),
      tco2TonReduction: roundN(sum(rows.map((r) => numOrZero(r.tco2_in_tons_reduction))), 4),
      totalWeightTons: round1(totalWeightTonsAll),
      finalReductionPct: round1(rows.length ? average(rows.map((r) => numOrZero(r.final_reduction) * 100)) : 0),
      cumulativeReduction: roundN(cumulative, 4),
    };
    return { monthlyRows, totalRow };
  }

  computeDerived(rows) {
    const months = monthRange(this.state.filters.startDate, this.state.filters.endDate);

    const monthGroups = groupBy(rows, (r) => {
      const d = rowDateOf(r);
      return d ? d.format('YYYY-MM') : null;
    });
    const monthly = months.map((m) => {
      const items = safeArray(monthGroups.get(m));
      const totalEmission = sum(items.map(emissionOf));
      const totalWeightKg = sum(items.map(weightKgOf));
      const totalWeightTons = totalWeightKg / 1000;
      return {
        label: moment(m, 'YYYY-MM').format('MMM YYYY'),
        totalEmission: roundN(totalEmission, 4),
        totalWeightKg: round1(totalWeightKg),
        tco2PerTon: roundN(totalWeightTons > 0 ? totalEmission / totalWeightTons : 0, 4),
      };
    });

    const movementGroups = groupBy(rows, movementCategoryOf);
    const movementDist = Array.from(movementGroups.entries())
      .map(([name, items]) => ({ name, totalEmission: roundN(sum(items.map(emissionOf)), 4) }))
      .sort((a, b) => b.totalEmission - a.totalEmission);

    const totalEmission = sum(rows.map(emissionOf));
    const totalWeightTons = sum(rows.map(weightKgOf)) / 1000;
    const tco2PerTon = totalWeightTons > 0 ? totalEmission / totalWeightTons : 0;
    const finalReductionPct = rows.length ? average(rows.map((r) => numOrZero(r.final_reduction) * 100)) : 0;

    // Baseline constants are real per-row fields, not hardcoded here - just
    // taken from the first row seen per entity group, since they're
    // confirmed constant within a group (same base_line_emission/
    // base_line_gross_co2 value repeats across every row sharing an
    // entity group in every sample checked).
    const entityGroups = groupBy(rows, entityGroupOf);
    const baseline = Array.from(entityGroups.entries()).map(([group, items]) => ({
      group,
      baseLineEmissionPct: items[0] ? numOrZero(items[0].base_line_emission) : 0,
      baseLineGrossCo2: items[0] ? numOrZero(items[0].base_line_gross_co2) : 0,
    }));

    const liIaVaRows = rows.filter((r) => entityGroupOf(r) === 'LI+IA+VA');
    const numericRows = rows.filter((r) => entityGroupOf(r) === 'NUMERIC');

    return {
      totalRecords: rows.length,
      monthly,
      movementDist,
      totalEmission: roundN(totalEmission, 4),
      totalWeightTons: round1(totalWeightTons),
      tco2PerTon: roundN(tco2PerTon, 4),
      finalReductionPct: round1(finalReductionPct),
      baseline,
      liIaVa: this.buildEntityGroupRows(liIaVaRows, months),
      numeric: this.buildEntityGroupRows(numericRows, months),
    };
  }

  onFilterChange = (key, value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [key]: value } }), () => this.applyClientFilters());
  };

  onApply = () => { this.loadData(); };

  onReset = () => {
    this.setState({
      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        trackingMode: TRACKING_MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },
    }, () => this.loadData());
  };

  renderPendingWidget(title, subtitle, needs) {
    return (
      <div className="pdm-card">
        <p className="pdm-card-title">{title}</p>
        <p className="pdm-card-subtitle">{subtitle}</p>
        <div className="pdm-empty-state">
          <div className="pdm-empty-state-icon">&#9679;</div>
          <strong>Pending data source</strong>
          <span>{needs}</span>
        </div>
      </div>
    );
  }

  renderEmptyState(message, hint) {
    return (
      <div className="pdm-empty-state">
        <div className="pdm-empty-state-icon">&#9679;</div>
        <strong>{message}</strong>
        {hint && <span>{hint}</span>}
      </div>
    );
  }

  renderChartCard(title, subtitle, options, height = 320) {
    return (
      <div className="pdm-card">
        {title && <p className="pdm-card-title">{title}</p>}
        {subtitle && <p className="pdm-card-subtitle">{subtitle}</p>}
        {options ? (
          <div className="pdm-chart-flex" style={{ height }}>
            <HighchartsReact
              highcharts={Highcharts}
              containerProps={{ style: { width: '100%', height: '100%' } }}
              options={{ ...options, chart: { ...options.chart, height } }}
              updateArgs={[true, true, true]}
            />
          </div>
        ) : this.renderEmptyState('No data available', 'Try widening the date range or clearing a filter.')}
      </div>
    );
  }

  buildMovementDonutOptions(items) {
    if (!items.length) return null;
    const total = roundN(sum(items.map((i) => i.totalEmission)), 4);
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'pie' },
      title: {
        useHTML: true,
        text: `<div style="text-align:center;line-height:1.2"><div style="font-size:18px;font-weight:700;color:${COLORS.ink}">${total}</div><div style="font-size:11px;color:${COLORS.muted}">Tons CO2</div></div>`,
        align: 'center',
        verticalAlign: 'middle',
        y: 6,
      },
      tooltip: { pointFormat: '<b>{point.y}</b> Tons CO2 ({point.percentage:.1f}%)' },
      legend: { enabled: true, itemStyle: { color: COLORS.ink, fontSize: '12px' } },
      plotOptions: { pie: { innerSize: '65%', borderWidth: 2, borderColor: '#fff', dataLabels: { enabled: false } } },
      series: [{
        name: 'Total Emission',
        data: items.map((item, idx) => ({ name: item.name, y: item.totalEmission, color: [COLORS.blue, COLORS.orange, COLORS.aqua, COLORS.violet, COLORS.magenta][idx % 5] })),
      }],
    };
  }

  buildMonthlyLineOptions(monthly, key, yTitle, color, suffix) {
    if (!monthly.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'line' },
      title: { text: null },
      xAxis: baseAxisLine(monthly.map((m) => m.label)),
      yAxis: baseValueAxis(yTitle),
      legend: { enabled: false },
      plotOptions: { series: { dataLabels: { enabled: true, style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } } },
      tooltip: { headerFormat: '', pointFormat: `<b>{point.key}</b><br/>${yTitle}: <b>{point.y}${suffix || ''}</b>` },
      series: [{ name: yTitle, data: monthly.map((m) => m[key]), color, marker: { enabled: true } }],
    };
  }

  buildEntityGroupColumnDefs() {
    return [
      { headerName: 'Year-Month', field: 'yearMonth', pinned: 'left', width: 130 },
      { headerName: 'Total Emission (Tons)', field: 'totalEmission', width: 170 },
      { headerName: 'TCO2PerTon', field: 'tco2PerTon', width: 130 },
      { headerName: 'Emission Reduction (%)', field: 'emissionReductionPct', width: 180, valueFormatter: (p) => (p.value !== undefined ? `${p.value}%` : '') },
      { headerName: 'TCo2-Ton Reduction', field: 'tco2TonReduction', width: 160 },
      { headerName: 'Total Weight (Tons)', field: 'totalWeightTons', width: 160 },
      { headerName: 'Final Reduction (%)', field: 'finalReductionPct', width: 160, valueFormatter: (p) => (p.value !== undefined ? `${p.value}%` : '') },
      { headerName: 'Cumulative Reduction', field: 'cumulativeReduction', width: 170 },
    ];
  }

  render() {
    const { state } = this;
    const f = state.filters;
    const fo = state.filterOptions;
    const d = state.derived;
    const routeNote = 'No "Route" field exists in the raw data (Power BI derives it internally) and no whitelist of which lanes count as "Plant to RWHS" has been confirmed yet.';
    const entitySummaryNote = 'No Area/CSR Area field exists anywhere in this feed - looks like separate master data not yet located. The 2024 vs 2025 comparison also needs a wider historical query than a single date-range widget covers.';

    return (
      <>
        {state.loading && (
          <div className="pdm-loading-overlay"><div className="pdm-spinner" /></div>
        )}
        {state.error && <div className="pdm-error-banner">{state.error}</div>}

        <div className="pdm-filterbar">
          <div className="pdm-field">
            <label className="pdm-filter-required">From</label>
            <DatePicker className="pdm-datepicker" selected={f.startDate} onChange={(v) => this.onFilterChange('startDate', v)} dateFormat="dd/MM/yyyy" maxDate={f.endDate} />
          </div>
          <div className="pdm-field">
            <label className="pdm-filter-required">To</label>
            <DatePicker className="pdm-datepicker" selected={f.endDate} onChange={(v) => this.onFilterChange('endDate', v)} dateFormat="dd/MM/yyyy" minDate={f.startDate} maxDate={new Date()} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Entity</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.entity} options={fo.entity} onChange={(v) => this.onFilterChange('entity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Movement Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.movementType} options={fo.movementType} onChange={(v) => this.onFilterChange('movementType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceZone} options={fo.sourceZone} onChange={(v) => this.onFilterChange('sourceZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceCity} options={fo.sourceCity} onChange={(v) => this.onFilterChange('sourceCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationZone} options={fo.destinationZone} onChange={(v) => this.onFilterChange('destinationZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter Category</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporterCategory} options={fo.transporterCategory} onChange={(v) => this.onFilterChange('transporterCategory', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Invoice Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.invoiceType} options={fo.invoiceType} onChange={(v) => this.onFilterChange('invoiceType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporter} options={fo.transporter} onChange={(v) => this.onFilterChange('transporter', v || [])} />
          </div>
          <div className="pdm-field">
            <label>Tracking Mode (FTL/PTL)</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.trackingMode} options={TRACKING_MODE_OPTIONS} onChange={(v) => this.onFilterChange('trackingMode', v)} />
          </div>
          <div className="pdm-filter-toolbar">
            <button className="pdm-btn pdm-btn-ghost" onClick={this.onReset}>Reset</button>
            <button className="pdm-btn pdm-btn-primary" onClick={this.onApply}>Apply Filters</button>
          </div>
        </div>

        {!d ? (
          <div className="pdm-card">{this.renderEmptyState('Loading CO2 dashboard...', null)}</div>
        ) : (
          <>
            <div className="pdm-notice-banner">
              {d.baseline.map((b) => `${b.group}: ${b.baseLineEmissionPct}% (TCo2/Ton) baseline, ${b.baseLineGrossCo2} Gross CO2`).join(' | ') || 'No baseline data for the current filters.'}
            </div>

            <div className="pdm-kpi-grid">
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Total Emission (Tons)</div>
                <div className="pdm-kpi-value">{d.totalEmission}</div>
                <div className="pdm-kpi-sub">Current filters</div>
              </div>
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">TCO2PerTon</div>
                <div className="pdm-kpi-value">{d.tco2PerTon}</div>
                <div className="pdm-kpi-sub">Total Emission / Total Weight (Tons)</div>
              </div>
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Final Reduction</div>
                <div className="pdm-kpi-value">{d.finalReductionPct}%</div>
                <div className="pdm-kpi-sub">Average of per-shipment final_reduction</div>
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                {this.renderChartCard(
                  'Carbon Footprint Distribution Across Movements',
                  'Donut - Total Emission (Tons) by movement type.',
                  this.buildMovementDonutOptions(d.movementDist),
                  320,
                )}
                {this.renderChartCard(
                  'Monthly TCO2 Emissions Analysis',
                  'Line chart - Total Emission (Tons) by month.',
                  this.buildMonthlyLineOptions(d.monthly, 'totalEmission', 'Total Emission (Tons)', COLORS.red, ''),
                  320,
                )}
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                {this.renderChartCard(
                  'Monthly TCO2 per Ton (%) Analysis',
                  'Line chart - TCo2/Ton by month.',
                  this.buildMonthlyLineOptions(d.monthly, 'tco2PerTon', 'TCO2PerTon', COLORS.violet, ''),
                  320,
                )}
                {this.renderChartCard(
                  'Monthly Dispatched Weight (in KGs)',
                  'Sum of Total Weight (KG) by month.',
                  this.buildMonthlyLineOptions(d.monthly, 'totalWeightKg', 'Total Weight (KG)', COLORS.aqua, ''),
                  320,
                )}
              </div>
            </div>

            <div className="pdm-section">
              {this.renderPendingWidget('Plant to RWHS - Direct Dispatch Weight (in KGs)', 'Line chart - total weight, Plant-to-RWHS direct dispatch routes only.', routeNote)}
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>Total Dispatches (LI+IA+VA)</h2>
                  <p>Year-Month / Total Emission / TCO2PerTon / Emission Reduction / TCo2-Ton Reduction / Total Weight (Tons) / Final Reduction / Cumulative Reduction.</p>
                </div>
              </div>
              <GridCard
                title="Total Dispatches (LI+IA+VA)"
                loading={state.loading}
                onRefresh={this.loadData}
                height={420}
                records={d.liIaVa.monthlyRows}
                columnDefs={this.buildEntityGroupColumnDefs()}
                pinnedBottomRowData={[d.liIaVa.totalRow]}
              />
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>Total Dispatches (Numeric)</h2>
                  <p>Same columns as LI+IA+VA, scoped to the Numeric entity.</p>
                </div>
              </div>
              <GridCard
                title="Total Dispatches (Numeric)"
                loading={state.loading}
                onRefresh={this.loadData}
                height={420}
                records={d.numeric.monthlyRows}
                columnDefs={this.buildEntityGroupColumnDefs()}
                pinnedBottomRowData={[d.numeric.totalRow]}
              />
            </div>

            <div className="pdm-section">
              {this.renderPendingWidget('Plant to RWHS - Direct Dispatches Lane Wise', 'Route / Year-Month / Total Emission.', routeNote)}
            </div>

            <div className="pdm-section">
              {this.renderPendingWidget(
                'Entity Summary',
                'Entity / Area / CSR Area / Tons Shipped 2024 / TCO2 2024 / Ratio 2024 / Tons Shipped 2025 / TCO2 2025 / Ratio 2025 / KPI1 / KPI2 / KPI2 Capped / Final Reduction - with a Total row.',
                entitySummaryNote,
              )}
            </div>
          </>
        )}
      </>
    );
  }
}
